import * as ko from 'knockout';
import * as localisationHelper from 'localisationHelper';
import * as Signals from 'signals';

import "@legend/web-components/modal";
import "@legend/web-components/button";

// Services
import { ConfigurableTextService } from "Scripts/ts/Localisation/ConfigurableTextService";
import { ValidationService } from "Content/ts/Shared/Services/Validation/validationService";
import { SiteConfigurationSettingService } from 'Content/ts/Shared/Services/SiteConfigurationSetting/siteConfigurationSettingService';

// Helpers
import { StringFunctions } from "Content/ts/Shared/Functions/stringFunctions";

// Components
import 'Content/ts/shared/ConfirmationModal/confirmation-modal';
import { DirectDebitConfirmationConfiguration } from 'Content/ts/Models/Shared/DirectDebitDetails/DirectDebitConfirmationConfiguration';

export interface BankDetailsParams {
    AccountName: KnockoutObservable<string>;
    AccountNumber: KnockoutObservable<string>;
    SortCode: KnockoutObservable<string>;
    ValidationFields: KnockoutObservable<any>[];
    UpdateSignal: Signals.Signal;
    DirectDebitInitialise: Signals.Signal;
    AutoInitialise: boolean;
    IsRequired: KnockoutObservable<boolean>;
    IsDisabled: KnockoutComputed<boolean>;
    DirectDebitConfirmationConfiguration: DirectDebitConfirmationConfiguration;
}

export class DDBacs {
    customMessages: {
        AccountNumber: string;
        SortCode: string;
        AccountNumberValidation: string;
        AccountNumberInvalid: string;
        SortCodeValidation: string;
        SortCodeInvalid: string;
        ModalTitle: string;
        ModalContent: string;
        CancelButtonText: string;
        DirectDebitLogoAlternativeText: string;
        DirectDebitGuarantee: string;
        DirectDebitConfirmationHeader: string;
        DirectDebitConfirmationAuthorise: string;
        DirectDebitConfirmationDeclaration: string;
        AccountHolderName: string;
        DirectDebitConfirmationModalTitle: string;
    };

    accountName: KnockoutObservable<string>;
    accountNumber: KnockoutObservable<string>;
    sortCode: KnockoutObservable<string>;

    accountNumberCopy: string;
    sortCodeCopy: string;

    validationFields: KnockoutObservable<any>[];
    showDirectDebitModal: KnockoutObservable<boolean>;
    isRequired: KnockoutObservable<boolean>;
    autoInitialise: boolean;
    firstLoad: boolean;

    isDisabled: KnockoutObservable<boolean>;

    showModal: KnockoutObservable<boolean>;
    updateSignal: Signals.Signal;
    directDebitInitialise: Signals.Signal;

    directDebitConfirmationConfiguration: DirectDebitConfirmationConfiguration;
    constructor(params: BankDetailsParams) {
        this.accountName = params.AccountName;
        this.accountNumber = params.AccountNumber;
        this.sortCode = params.SortCode;

        this.accountNumberCopy = params.AccountNumber().slice();
        this.sortCodeCopy = params.SortCode().slice();

        this.updateSignal = params.UpdateSignal;
        this.validationFields = params.ValidationFields;
        this.directDebitInitialise = params.DirectDebitInitialise;

        this.directDebitConfirmationConfiguration = params.DirectDebitConfirmationConfiguration;

        if (this.directDebitConfirmationConfiguration) {
            this.directDebitConfirmationConfiguration.showDirectDebitModalSignal.add(this.handleConfirmationModal.bind(this));
        }

        this.autoInitialise = params.AutoInitialise ?? true;
        this.isRequired = params.IsRequired;

        this.isDisabled = params.IsDisabled ?? ko.observable(false);

        this.showDirectDebitModal = ko.observable(false);
        this.firstLoad = true;

        this.showModal = ko.observable(false);
        this.retrieveCustomMessages();

        if (this.autoInitialise) {
            this.initialise();
        }
        else {
            this.directDebitInitialise.add(this.initialiseSignalMethod);
        }

        //only get the setting value if the confirmation modal config is supplied
        if (this.directDebitConfirmationConfiguration) {
            SiteConfigurationSettingService.GetSetting("LOC_SignUpDDConfirmation")
                .then((result: boolean) => {
                    this.directDebitConfirmationConfiguration.requiresConfirmation = result;
                })
                .catch(() => this.directDebitConfirmationConfiguration.requiresConfirmation = false);
        }
    }

    private handleConfirmationModal() {
        if (this.directDebitConfirmationConfiguration.requiresConfirmation) {
            this.showModal(true);
        }
    }

    private initialiseSignalMethod = (enabled: boolean): void => {
        if (enabled) {
            this.initialise();
        }
        else {
            if (!this.firstLoad) {
                this.dispose();
            }
        }
    };

    //data is not needed, but the event is. 
    modalClose(_:unknown , event:Event) {
        this.showModal(false);

        //scroll to the DD form
        const eventElement = event?.target as HTMLElement;
        if (eventElement) {
            eventElement.closest(".direct-debit").scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }

    modalOpen() {
        this.showModal(true);
    }

    modalConfirm() {
        this.directDebitConfirmationConfiguration.callback();
        this.modalClose(null, null);
    }

    private retrieveCustomMessages(): void {
        this.customMessages = {
            AccountNumber: StringFunctions.ToSentenceCase(localisationHelper.getString("GenericResources", "AccountNumber")),
            SortCode: StringFunctions.ToSentenceCase(localisationHelper.getString("GenericResources", "SortCode")),
            AccountNumberValidation: localisationHelper.getString("GenericResources", "Validation_AccountNumber"),
            AccountNumberInvalid: localisationHelper.getString("GenericResources", "InvalidAccountNumber"),
            SortCodeValidation: localisationHelper.getString("GenericResources", "Validation_SortCode"),
            SortCodeInvalid: localisationHelper.getString("GenericResources", "InvalidSortCode"),
            ModalTitle: localisationHelper.getString("GenericResources", "DirectDebitGuarantee"),
            ModalContent: ConfigurableTextService.RetrieveTextSync("webcopyDDGuarantee"),
            CancelButtonText: localisationHelper.getString("GenericResources", "Cancel"),
            DirectDebitLogoAlternativeText: localisationHelper.getString("GenericResources", "DirectDebitLogoAlternativeText"),
            DirectDebitGuarantee: localisationHelper.getString("GenericResources", "DirectDebitGuarantee"),
            DirectDebitConfirmationHeader: localisationHelper.getString("GenericResources", "DirectDebitConfirmationHeader"),
            DirectDebitConfirmationAuthorise: localisationHelper.getString("GenericResources", "DirectDebitConfirmationAuthorise"),
            DirectDebitConfirmationDeclaration: localisationHelper.getString("GenericResources", "DirectDebitConfirmationDeclaration"),
            AccountHolderName: StringFunctions.ToSentenceCase(localisationHelper.getString("GenericResources", "AccountHolderName")),
            DirectDebitConfirmationModalTitle: localisationHelper.getString("GenericResources", "DirectDebit"),
        }
    }

    private setupValidationFields(): void {
        ValidationService.AddValidationRule(this.accountNumber, this.validationFields, {
            maxLength: {
                params: 8,
                message: this.customMessages.AccountNumberValidation
            },
            pattern: {
                message: this.customMessages.AccountNumberInvalid,
                params: /^[0-9]*$/
            }
        });

        ValidationService.AddValidationRule(this.sortCode, this.validationFields, {
            maxLength: {
                params: 6,
                message: this.customMessages.SortCodeValidation
            },
            pattern: {
                message: this.customMessages.SortCodeInvalid,
                params: ValidationService.SortCodeRegex
            }
        });
    }

    private removeValidationFields(): void {
        ValidationService.RemoveValidationRule(this.accountNumber, this.validationFields);
        ValidationService.RemoveValidationRule(this.sortCode, this.validationFields);
    }

    // These subscriptions need to be created after the validation so that
    // the validation can run before the subscription is fired. If not the knockout
    // validation will still be validating on the previous value
    private setupSubscriptions(): void {
        this.accountNumber.subscribe(() => {
            this.updateSignal.dispatch();
        });

        this.sortCode.subscribe(() => {
            this.updateSignal.dispatch();
        });
    }

    showDirectDebitGuarantee(): void {
        this.showDirectDebitModal(true);
    }

    handleSortNumClick(): void {
        if (this.sortCodeCopy === this.sortCode()) {
            this.sortCode("");
        }
    }

    handleAccNumClick(): void {
        if (this.accountNumberCopy === this.accountNumber()) {
            this.accountNumber("");
        }
    }

    private initialise(): void {
        this.setupValidationFields();
        this.setupSubscriptions();
        this.firstLoad = false;
    }

    public dispose(): void {
        this.removeValidationFields();
        this.directDebitInitialise.remove(this.initialiseSignalMethod);
    }
}

const componentName = 'dd-bacs';
ko.components.unregister(componentName);
ko.components.register(componentName, {
    viewModel: DDBacs,
    template: { require: `text!/Themes/Default/Content/ts/Shared/DirectDebitDetails/${componentName}.html` }
});
